import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import RootRoute from "./routes/RootRoute";
import { PUBLIC_HOME_PATH } from "./routes/public-home-path";
import HomePage from "./routes/HomePage";
import LoginPage from "./routes/LoginPage";
import GoogleCallbackPage from "./routes/GoogleCallbackPage";
import OnboardingPage from "./routes/OnboardingPage";
import PrivacyPolicyPage from "./routes/PrivacyPolicyPage";
import TermsPage from "./routes/TermsPage";
import AboutPage from "./routes/AboutPage";
import ScrollToTop from "./routes/ScrollToTop";
import TeacherLayout from "./teacher/TeacherLayout";
import TeacherOverviewPage from "./teacher/TeacherOverviewPage";
import TeacherCohortsListPage from "./teacher/TeacherCohortsListPage";
import TeacherCohortPage from "./teacher/TeacherCohortPage";
import TeacherCoursesPage from "./teacher/TeacherCoursesPage";
import TeacherLessonsPage from "./teacher/TeacherLessonsPage";
import TeacherCourseDetailsPage from "./teacher/TeacherCourseDetailsPage";
import TeacherCourseLayout from "./teacher/TeacherCourseLayout";
import TeacherQuizEditorPage from "./teacher/TeacherQuizEditorPage";
import TeacherPresentationEditorPage from "./teacher/TeacherPresentationEditorPage";
import TeacherQuizResultsPage from "./teacher/TeacherQuizResultsPage";
import TeacherQuizPreviewPage from "./routes/TeacherQuizPreviewPage";
import TeacherPresentationPreviewPage from "./routes/TeacherPresentationPreviewPage";
import TeacherCourseAiStudioPage from "./teacher/TeacherCourseAiStudioPage";
import LessonWorkspacePage from "./teacher/lesson-workspace/LessonWorkspacePage";
import TeacherDeletedLessonsPage from "./teacher/TeacherDeletedLessonsPage";
import TeacherSubmissionsPage from "./teacher/TeacherSubmissionsPage";
import TeacherLessonSubmissionsPage from "./teacher/TeacherLessonSubmissionsPage";
import TeacherApiKeysPage from "./teacher/TeacherApiKeysPage";
import TeacherBillingPage from "./teacher/TeacherBillingPage";
import TeacherSandboxPage from "./teacher/TeacherSandboxPage";
import AdminCommunityReviewPage from "./admin/AdminCommunityReviewPage";
import AdminStandardsPage from "./admin/AdminStandardsPage";
import AdminStandardSetDetailPage from "./admin/AdminStandardSetDetailPage";
import AdminLayout from "./admin/AdminLayout";
import AdminConsolePage from "./admin/AdminConsolePage";
import AdminUsersPage from "./admin/AdminUsersPage";
import ImpersonationBanner from "./admin/ImpersonationBanner";
import TeacherLessonPreviewPage from "./routes/TeacherLessonPreviewPage";
import TeacherSubmissionRunPage from "./routes/TeacherSubmissionRunPage";
import StudentLessonsPage from "./routes/StudentLessonsPage";
import StudentJoinPage from "./routes/StudentJoinPage";
import StudentIdePage from "./routes/StudentIdePage";
import StudentQuizPage from "./routes/StudentQuizPage";
import StudentPresentationPage from "./routes/StudentPresentationPage";
import NoActiveClassesPage from "./routes/NoActiveClassesPage";
import PlaygroundListRoute from "./routes/PlaygroundListRoute";
import PlaygroundListPage from "./playground/PlaygroundListPage";
import PlaygroundIdePage from "./playground/PlaygroundIdePage";
import QuickScriptPage from "./playground/QuickScriptPage";
import CohortPlaygroundListPage from "./playground/CohortPlaygroundListPage";
import CohortPlaygroundProjectPage from "./playground/CohortPlaygroundProjectPage";
import StudentLayout from "./student/StudentLayout";
import StudentCohortsPage from "./routes/StudentCohortsPage";
import StudentCohortDetailPage from "./routes/StudentCohortDetailPage";
import StudentCohortsRedirectPage from "./routes/StudentCohortsRedirectPage";
import CohortStudentPlaygroundsPage from "./playground/CohortStudentPlaygroundsPage";
import { RequireGoogleAuth, RequireRole, RequireAdmin } from "./routes/route-guards";
import { LessonLaunchProvider } from "./transition/LessonLaunchProvider";

function RedirectTemplateLessonToLibrary() {
  const { lessonId } = useParams();
  return <Navigate to={`/teacher/lessons/${lessonId ?? ""}`} replace />;
}

function RedirectLessonAiToWorkspace() {
  const { lessonId } = useParams();
  return <Navigate to={`/teacher/lessons/${lessonId ?? ""}`} replace />;
}

function RedirectLegacyCourse() {
  const { courseId } = useParams();
  const location = useLocation();
  const suffix = courseId
    ? location.pathname.replace(`/teacher/curriculum/${courseId}`, "")
    : "";
  return <Navigate to={`/teacher/courses/${courseId ?? ""}${suffix}`} replace />;
}

function RedirectLegacyCohort() {
  const { cohortId } = useParams();
  return <Navigate to={`/teacher/cohorts/${cohortId ?? ""}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <LessonLaunchProvider>
        <div className="app-shell">
        <ImpersonationBanner />
        <div className="app-shell-body">
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path={PUBLIC_HOME_PATH} element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/auth/callback/google" element={<GoogleCallbackPage />} />
          <Route
            path="/onboarding"
            element={
              <RequireGoogleAuth>
                <OnboardingPage />
              </RequireGoogleAuth>
            }
          />

          <Route
            path="/teacher"
            element={
              <RequireRole allow={["teacher"]}>
                <TeacherLayout />
              </RequireRole>
            }
          >
            <Route index element={<TeacherOverviewPage />} />
            <Route path="cohorts" element={<TeacherCohortsListPage />} />
            <Route path="cohorts/:cohortId" element={<TeacherCohortPage />} />
            <Route path="cohorts/:cohortId/quizzes/:quizId" element={<TeacherQuizResultsPage />} />
            <Route path="cohorts/:cohortId/quizzes/:quizId/preview" element={<TeacherQuizPreviewPage />} />
            <Route path="cohorts/:cohortId/presentations/:presentationId/preview" element={<TeacherPresentationPreviewPage />} />
            <Route path="cohorts/:cohortId/students/:studentUserId/playgrounds" element={<CohortStudentPlaygroundsPage />} />
            <Route path="cohorts/:cohortId/playground" element={<CohortPlaygroundListPage />} />
            <Route path="cohorts/:cohortId/playground/:projectId" element={<CohortPlaygroundProjectPage />} />
            <Route path="cohorts/:cohortId/lessons/:lessonId/submissions" element={<TeacherLessonSubmissionsPage />} />
            <Route path="playground" element={<PlaygroundListPage />} />
            <Route path="lessons" element={<TeacherLessonsPage />} />
            <Route path="lessons/deleted" element={<TeacherDeletedLessonsPage />} />
            <Route path="lessons/new" element={<LessonWorkspacePage />} />
            <Route path="lessons/new/ai" element={<Navigate to="/teacher/lessons/new" replace />} />
            <Route path="lessons/:lessonId/ai" element={<RedirectLessonAiToWorkspace />} />
            <Route path="lessons/:lessonId" element={<LessonWorkspacePage />} />
            <Route
              path="community/lessons"
              element={<Navigate to="/teacher/lessons?tab=community" replace />}
            />
            <Route
              path="community/courses"
              element={<Navigate to="/teacher/courses?tab=community" replace />}
            />
            <Route path="courses" element={<TeacherCoursesPage />} />
            <Route path="courses/new/ai" element={<TeacherCourseAiStudioPage />} />
            <Route path="courses/:courseId/manage" element={<TeacherCourseLayout />} />
            <Route path="courses/:courseId/quizzes/:quizId/preview" element={<TeacherQuizPreviewPage />} />
            <Route path="courses/:courseId/quizzes/:quizId" element={<TeacherQuizEditorPage />} />
            <Route path="courses/:courseId/presentations/:presentationId/preview" element={<TeacherPresentationPreviewPage />} />
            <Route path="courses/:courseId/presentations/:presentationId" element={<TeacherPresentationEditorPage />} />
            <Route path="courses/:courseId" element={<TeacherCourseDetailsPage />} />
            <Route
              path="courses/:courseId/lessons/:lessonId"
              element={<RedirectTemplateLessonToLibrary />}
            />
            <Route path="submissions" element={<TeacherSubmissionsPage />} />
            <Route path="api-keys" element={<TeacherApiKeysPage />} />
            <Route path="billing" element={<TeacherBillingPage />} />
            <Route path="sandbox" element={<TeacherSandboxPage />} />
          </Route>

          <Route path="/dashboard" element={<Navigate to="/teacher" replace />} />
          <Route path="/dashboard/curriculum" element={<Navigate to="/teacher/courses" replace />} />
          <Route path="/teacher/curriculum" element={<Navigate to="/teacher/courses" replace />} />
          <Route path="/teacher/curriculum/:courseId/*" element={<RedirectLegacyCourse />} />
          <Route path="/teacher/classes" element={<Navigate to="/teacher/cohorts" replace />} />
          <Route path="/teacher/classes/:classId/cohorts/:cohortId" element={<RedirectLegacyCohort />} />
          <Route path="/teacher/classes/:classId" element={<Navigate to="/teacher/cohorts" replace />} />

          <Route
            path="/admin"
            element={
              <RequireRole allow={["teacher"]}>
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              </RequireRole>
            }
          >
            <Route index element={<AdminConsolePage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="community" element={<AdminCommunityReviewPage />} />
            <Route path="standards" element={<AdminStandardsPage />} />
            <Route path="standards/:setId" element={<AdminStandardSetDetailPage />} />
          </Route>

          <Route
            path="/lesson-preview"
            element={
              <RequireRole allow={["teacher"]}>
                <TeacherLessonPreviewPage />
              </RequireRole>
            }
          />
          <Route
            path="/teacher/cohorts/:cohortId/lessons/:lessonId/submissions/:attemptId/run"
            element={
              <RequireRole allow={["teacher"]}>
                <TeacherSubmissionRunPage />
              </RequireRole>
            }
          />

          <Route
            path="/playground"
            element={
              <RequireRole allow={["teacher", "student"]}>
                <PlaygroundListRoute />
              </RequireRole>
            }
          />
          <Route
            path="/playground/quick"
            element={
              <RequireRole allow={["teacher", "student"]}>
                <QuickScriptPage />
              </RequireRole>
            }
          />
          <Route
            path="/playground/:projectId"
            element={
              <RequireRole allow={["teacher", "student"]}>
                <PlaygroundIdePage />
              </RequireRole>
            }
          />

          <Route
            element={
              <RequireRole allow={["student"]}>
                <StudentLayout />
              </RequireRole>
            }
          >
            <Route path="/lessons" element={<StudentLessonsPage />} />
            <Route path="/lessons/cohorts" element={<StudentCohortsPage />} />
            <Route path="/cohorts" element={<StudentCohortsRedirectPage />} />
            <Route path="/cohorts/:cohortId" element={<StudentCohortDetailPage />} />
            <Route path="/join" element={<StudentJoinPage />} />
            <Route path="/presentation" element={<StudentPresentationPage />} />
          </Route>
          <Route path="/projects" element={<Navigate to="/lessons" replace />} />
          <Route
            path="/ide"
            element={
              <RequireRole allow={["student"]}>
                <StudentIdePage />
              </RequireRole>
            }
          />
          <Route
            path="/quiz"
            element={
              <RequireRole allow={["student"]}>
                <StudentQuizPage />
              </RequireRole>
            }
          />
          <Route
            path="/no-active-classes"
            element={
              <RequireRole allow={["student"]}>
                <NoActiveClassesPage />
              </RequireRole>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
        </div>
        </LessonLaunchProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
